<template>
  <div class="card">
    <div class="card-header bg-primary d-flex justify-content-between">
      <h5 class="mb-0 text-white">Investigation Interview Dashboard</h5>
      <div class="d-flex justify-content-end">
        <InterviewsExportButton v-if="showForNonProctors" :investigation="investigation" />
        <InterviewsImportButton v-if="investigationIsOpen" :investigation="investigation" @reload="$emit('reload')" class="mx-2" />
      </div>
    </div>
    <div class="card-body">
      <ErrorSummary :error="error" />
      <div class="alert alert-dark" role="alert" v-if="!investigationIsOpen">
        You may not add, edit, or send interviews on a closed investigation.
      </div>
      <div v-if="investigationIsOpen">
        <form ref="form" class="mt-3 needs-validation" :class="formWasValidated ? 'was-validated' : ''" novalidate @submit.prevent>
          <div class="form-row">
            <div class="form-group col-md-3">
              <label>Registration ID</label>
              <input
                v-model="registrationId"
                type="text"
                class="form-control"
                :disabled="formloadingMessage"
                required
                @blur="getInterviewRegistrationIdDuplicate"
                data-bs-toggle="tooltip"
                data-bs-delay="500"
                title="Unique alphanumeric value for each test taker"
              />
              <div class="invalid-feedback">Please provide a registration ID.</div>
            </div>
            <div class="form-group col-md-6">
              <label>Email Address</label>
              <input v-model="emailAddress" type="email" class="form-control" :disabled="formloadingMessage" required />
              <div class="invalid-feedback">{{ emailAddressInvalidFeedback }}</div>
            </div>
            <div class="form-group col-md-3">
              <label>Language</label>
              <select v-model="languageCode" class="form-control" :disabled="formloadingMessage" required>
                <option value="en" selected>English</option>
                <option value="es">Spanish</option>
              </select>
              <div class="invalid-feedback">Please select a language.</div>
            </div>
          </div>
          <div class="form-group">
            <div class="form-check-inline" data-bs-toggle="tooltip" data-bs-delay="500" title="Provide test taker extra time to answer each question">
              <label class="form-check-label" for="extendedTime">
                Extended Time
              </label>
              <input v-model="extendedTime" id="extendedTime" class="form-check-input m-2" type="checkbox" :disabled="formloadingMessage" />
            </div>
          </div>
          <div v-if="extendedTime" class="form-group">
            <input
              v-model="extendedTimeReason"
              type="text"
              id="extendedTimeReason"
              class="form-control"
              placeholder="Reason for Extension"
              :disabled="formloadingMessage"
              required
              maxlength="255"
            />
            <div class="invalid-feedback">Please provide a reason for extended time.</div>
          </div>
          <div
            class="modal fade"
            id="duplicateRegistrationModal"
            data-backdrop="static"
            tabindex="-1"
            role="dialog"
            aria-labelledby="duplicateRegistrationLabel"
            aria-hidden="true"
          >
            <div class="modal-dialog" role="document">
              <div class="modal-content">
                <div class="modal-header">
                  <h5 class="modal-title" id="duplicateRegistrationLabel">Warning: Duplicate Registration ID Detected</h5>
                  <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div class="modal-body">
                  <p>
                    Please note that the Registration ID you have entered to create this interview has previously been used by this organization on
                    {{ formatter.date.short(this.previousDate) }}.
                  </p>
                  <p>
                    If you wish to proceed to create this interview, please click Add below. Otherwise, please provide a different Registration ID.
                  </p>
                </div>
                <div class="modal-footer">
                  <button type="button" class="btn btn-primary" data-dismiss="modal">Close</button>
                </div>
              </div>
            </div>
          </div>
          <div class="form-group">
            <button type="button" class="btn btn-sm btn-primary mt-1" @click="addInterview" :disabled="buttonIsDisabled" @keyup.enter="addInterview">
              <span v-if="formloadingMessage" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              <fa v-if="!formloadingMessage" icon="plus" fixed-width />
              <span class="mx-1">{{ formloadingMessage ? formloadingMessage : "Add" }}</span>
            </button>
          </div>
        </form>
      </div>
      <hr />
      <div class="row d-print-none mb-3">
        <div class="col">
          <div class="">
            <form class="form-inline w-60" @submit.prevent>
              <div class="input-group d-flex flex-fill">
                <input type="text" class="form-control" placeholder="Search by registration ID or email address" v-model="query" />
                <div class="input-group-append">
                  <button type="button" class="btn btn-primary ml-3" @click="getInterviews">
                    <fa icon="search" />
                    <span class="ml-2">Search</span>
                  </button>
                  <button type="button" class="btn btn-outline-primary ml-3" @click="getInterviews">
                    <fa icon="redo" />
                    <span class="ml-2">Refresh</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
        <div class="col">
          <div class="">
            <div class="d-flex justify-content-end">
              <button
                type="button"
                class="btn btn btn-primary"
                v-if="showForNonProctors && canSendAnyInterviews()"
                @click="sendAllInterviews"
                :disabled="isSendingAll"
              >
                <span v-if="!isSendingAll">
                  <fa icon="envelope" fixed-width />
                  Send Created Interviews</span
                >
                <span v-if="isSendingAll">
                  <fa icon="sync" fixed-width spin />
                  Sending Interviews</span
                >
              </button>
              <ResendIncompleteInterviewsButton
                v-if="canReSendIncompleteInterviews()"
                :investigation="investigation"
                :interviews="interviews"
                :isLoading="isLoading"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="table-responsive">
        <table class="table table-hover">
          <thead class="thead-light">
            <tr>
              <th v-if="showForNonProctors" scope="col"></th>
              <th scope="col">Actions</th>
              <th v-if="showForNonProctors" scope="col" class="text-center">Status</th>
              <th v-if="showForNonProctors" scope="col" class="text-center">Contact Me</th>
              <th v-if="showForNonProctors" scope="col" class="text-center">Score</th>
              <th v-if="showForNonProctors" scope="col" class="text-center">Admissions</th>
              <th v-if="showForNonProctors" scope="col" class="text-center">Skips</th>
              <th v-if="showForNonProctors" scope="col" class="text-center">Observations</th>
              <th scope="col">Registration ID</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loadingMessage && (!interviews || interviews.length === 0)" class="text-center text-muted">
              <td colspan="12"><fa icon="circle-notch" spin /> {{ loadingMessage }}</td>
            </tr>
            <tr v-if="!loadingMessage && interviews.length === 0" class="text-center text-muted">
              <td colspan="5">No results</td>
            </tr>
            <tr v-for="interview in interviews" :key="interview.organizationInvestigationInterviewId" :class="getBgClass(interview)">
              <td v-if="interview.loadingMessage" class="text-right text-muted">
                <fa icon="sync" fixed-width spin />
                {{ interview.loadingMessage }}
              </td>
              <td v-if="showForNonProctors" class="text-center">
                <span @click="onStarClick(interview)" v-if="interview.organizationReportId">
                  <fa class="gold-star" :icon="['fas', 'star']" fixed-width v-if="interview.isStarred" />
                  <fa :icon="['far', 'star']" fixed-width v-if="!interview.isStarred" />
                </span>
              </td>
              <td v-if="!interview.loadingMessage">
                <div class="dropdown" v-if="canStartInterview(interview)">
                  <button class="btn btn-sm btn-outline-primary dropdown-toggle" type="button" data-toggle="dropdown" :disabled="!investigationIsOpen">
                    <fa :icon="getActionButtonIcon(interview)" fixed-width />
                    {{ getActionButtonText(interview) }}
                  </button>
                  <div class="dropdown-menu">
                    <!-- before send options -->
                    <button type="button" class="dropdown-item" v-if="canStartInterview(interview)" @click="showStartInterviewModal(interview)">
                      <fa icon="laptop" fixed-width />
                      Start on this Device
                    </button>
                    <button type="button" class="dropdown-item" v-if="showForNonProctors && canSendInterview(interview)" @click="sendInterview(interview)">
                      <fa icon="envelope" fixed-width />
                      Send Interview
                    </button>
                    <button type="button" class="dropdown-item" v-if="showForNonProctors && canResendInterview(interview)" @click="sendInterview(interview)">
                      <fa icon="envelope" fixed-width />
                      Re-Send Interview
                    </button>
                    <button type="button" class="dropdown-item" v-if="canEditInterview(interview)" @click="editInterview(interview)">
                      <fa icon="pencil-alt" fixed-width />
                      Edit Interview
                    </button>
                    <div class="dropdown-divider" v-if="canDeleteInterview(interview)"></div>
                    <button
                      type="button"
                      class="dropdown-item text-danger"
                      v-if="showForNonProctors && canDeleteInterview(interview)"
                      @click="deleteInterview(interview)"
                    >
                      <fa icon="times" fixed-width />
                      Delete Interview
                    </button>
                  </div>
                </div>
                <button type="button" class="btn btn-sm btn-link" v-if="showForNonProctors && canViewReport(interview)" @click="viewReport(interview)">
                  <fa icon="external-link-alt" fixed-width />
                  View Report
                </button>
                <div v-if="!canViewReport(interview) && !canStartInterview(interview)">
                  <span class="badge" :class="getInterviewStatusBadgeStyle(interview)">{{ getInterviewStatusBadgeLabel(interview) }}</span>
                </div>
              </td>
              <td v-if="showForNonProctors" class="text-center">{{ getReportStatusDisplayName(interview) }}</td>
              <td v-if="showForNonProctors" class="text-center">
                <fa class="text-danger" icon="hand-pointer" fixed-width v-if="interview.hasRequestForContact" />
                <span class="text-muted" v-if="!interview.hasRequestForContact">---</span>
              </td>
              <td v-if="showForNonProctors" class="text-center">
                <fa :class="getScoreStyle(interview)" icon="circle" fixed-width v-if="showScore(interview)" />
                <span class="text-muted" v-if="!showScore(interview)">---</span>
              </td>
              <td v-if="showForNonProctors" class="text-center">
                <fa
                  class="text-danger"
                  icon="exclamation"
                  fixed-width
                  v-if="interview.hasAdmissions"
                  :title="getAdmissionsObservationsMouseover(interview.admissionCategories)"
                />
                <span class="text-muted" v-if="!interview.hasAdmissions">---</span>
              </td>
              <td v-if="showForNonProctors" class="text-center">
                <fa class="text-info" icon="info-circle" fixed-width v-if="interview.hasSkips" />
                <span class="text-muted" v-if="!interview.hasSkips">---</span>
              </td>
              <td v-if="showForNonProctors" class="text-center">
                <fa
                  class="text-warning"
                  icon="exclamation-triangle"
                  fixed-width
                  v-if="interview.hasObservations"
                  :title="getAdmissionsObservationsMouseover(interview.observationCategories)"
                />
                <span class="text-muted" v-if="!interview.hasObservations">---</span>
              </td>
              <td>{{ interview.registrationId }}</td>
            </tr>
          </tbody>
        </table>
        <div v-if="needsPaging" class="row">
          <div class="col">
            <div class="row justify-content-between p-3">
              <button type="button" class="btn btn-outline-dark" @click="prev" :disabled="!hasPrev">Previous</button>
              <button type="button" class="btn btn-outline-dark" @click="next" :disabled="!hasNext">Next</button>
            </div>
          </div>
        </div>
        <div
          class="modal fade"
          id="startInterviewOnDeviceModal"
          data-backdrop="static"
          tabindex="-1"
          role="dialog"
          aria-labelledby="startInterviewOnDeviceLabel"
          aria-hidden="true"
        >
          <div class="modal-dialog" role="document">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title" id="startInterviewOnDeviceLabel">Begin Interview on this Device</h5>
                <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div class="modal-body">
                <p>
                  Start the interview immediately on this device. You will be signed out and the interview will begin.
                </p>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" data-dismiss="modal" @click="closeStartInterviewModal()">Cancel</button>
                <button type="button" class="btn btn-primary" @click="startInterview()">Start</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <InterviewsEditModal ref="edit-modal" :investigation="investigation" @reload="getInterviews" />
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { mapActions } from "vuex";
import debounce from "debounce";
import formatter from "@/utils/formatter";
import ErrorSummary from "@/components/ErrorSummary.vue";
import ResendIncompleteInterviewsButton from "@/views/reports/investigations/edit-investigation/ResendIncompleteInterviewsButton.vue";
import InterviewsExportButton from "@/views/reports/investigations/edit-investigation/InterviewsExportButton.vue";
import InterviewsImportButton from "@/views/reports/investigations/edit-investigation/InterviewsImportButton.vue";
import InterviewsEditModal from "@/views/reports/investigations/edit-investigation/InterviewsEditModal.vue";
import $ from "jquery";

export default {
  components: {
    ErrorSummary,
    ResendIncompleteInterviewsButton,
    InterviewsExportButton,
    InterviewsImportButton,
    InterviewsEditModal,
  },
  props: {
    isLoading: Boolean,
    investigation: Object,
  },
  data() {
    return {
      loadingMessage: null,
      error: null,

      // form
      formloadingMessage: null,
      formWasValidated: false,
      registrationId: null,
      emailAddress: null,
      extendedTime: false,
      extendedTimeReason: null,
      languageCode: "en",

      // previously used
      previousDate: null,

      // table
      interviews: [],

      // start modal
      interviewToStart: null,

      // button states
      isSendingAll: false,

      page: {},
      skip: 0,
      take: 25,
      query: null,
    };
  },
  computed: {
    ...mapGetters("static", ["languages", "reportStatuses"]),
    ...mapGetters("role", [
      "isVerensicsAdministrator",
      "isOrganizationAdministrator",
      "isInvestigationCreator",
      "isInvestigationProctor",
      "isInvestigationViewer",
    ]),
    formatter: () => formatter,
    investigationIsOpen() {
      return this.investigation.statusCode === "open";
    },
    buttonIsDisabled() {
      return this.formloadingMessage;
    },
    emailAddressInvalidFeedback() {
      return "Please provide a valid email address.";
    },
    showForNonProctors() {
      // if any non-proctor roles, show all sections
      if (this.isVerensicsAdministrator || this.isOrganizationAdministrator || this.isInvestigationCreator || this.isInvestigationViewer) return true;
      // If only a proctor, hide non-proctor sections
      if (this.isInvestigationProctor) return false;
      // default to hide a section
      return false;
    },
    queryString() {
      let url = `skip=${this.skip}&take=${this.take}`;
      if (this.query) url += `&query=${encodeURIComponent(this.query)}`;
      return url;
    },
    hasPrev() {
      if (!this.page) return false;
      return this.skip > 0;
    },
    hasNext() {
      if (!this.page) return false;
      return this.skip + this.take < this.page.total;
    },
    needsPaging() {
      return this.hasNext || this.hasPrev;
    },
  },
  watch: {
    investigation() {
      this.extendedTime = this.investigation.extendedTimeDefault ?? false;
      this.extendedTimeReason = this.investigation.extendedTimeDefault == true ? "Extended time defaulted by organization." : null;
      this.getInterviews();
    },
    queryString() {
      this.getInterviews();
    },
    query() {
      this.skip = 0;
    },
  },
  created() {
    this.skip = this.$route.query.skip ? parseInt(this.$route.query.skip) : this.skip;
    this.take = this.$route.query.take ? parseInt(this.$route.query.take) : this.take;
    this.query = this.$route.query.query || this.query;

    this.getInterviews = debounce(this.getInterviews, 350);
  },
  methods: {
    ...mapActions("cognito", ["signOut"]),
    prev() {
      this.skip = Math.max(0, this.skip - this.take);
    },
    next() {
      this.skip += this.take;
    },
    onStarClick(interview) {
      if (!interview || !interview.organizationReportId) return;

      // flip the starred value
      interview.isStarred = !interview.isStarred;

      // save to server
      this.$axios
        .put(`/v1/organizations/${this.investigation.organizationId}/reports/${interview.organizationReportId}/star/${interview.isStarred.toString()}`)
        .catch(err => (this.error = err))
        .finally(() => {
          this.loadingMessage = null;
          interview.loadingMessage = null;
        });
    },
    getAdmissionsObservationsMouseover(categories) {
      if (categories == null) return "";
      var text = "";
      var categoriesLength = Object.keys(categories).length;
      for (const [key, value] of Object.entries(categories)) {
        text = text + `${key}: ${value}`;
        categoriesLength = categoriesLength - 1;
        if (categoriesLength > 0) {
          text = text + "\n";
        }
      }
      return text;
    },
    addInterviewReset() {
      this.formloadingMessage = null;
      this.formWasValidated = false;
    },
    formReset() {
      this.registrationId = null;
      this.emailAddress = null;
      this.extendedTime = this.investigation.extendedTimeDefault ?? false;
      this.extendedTimeReason = this.investigation.extendedTimeDefault == true ? "Extended time defaulted by organization." : null;
      this.languageCode = "en";
      this.previousDate = null;
    },
    addInterview() {
      // reset
      this.addInterviewReset();

      // validations
      if (!this.$refs.form.checkValidity()) {
        this.formWasValidated = true;
        return;
      }

      // payload
      const payload = {
        registrationId: this.registrationId,
        emailAddress: this.emailAddress,
        extendedTime: this.extendedTime,
        extendedTimeReason: this.extendedTimeReason,
        languageCode: this.languageCode,
      };

      // api
      this.formloadingMessage = "Saving...";
      this.$axios
        .post(`/v1/organizations/${this.investigation.organizationId}/investigations/${this.investigation.investigationId}/interviews`, payload)
        .then(() => {
          this.formReset();
          this.$emit("reload");
        })
        .catch(err => (this.error = err))
        .finally(() => {
          this.addInterviewReset();
        });
    },
    getInterviews() {
      this.loadingMessage = "Loading Interviews...";
      this.$axios
        .get(`/v1/organizations/${this.investigation.organizationId}/investigations/${this.investigation.investigationId}/interviews?${this.queryString}`)
        .then(res => {
          this.page = res.data;
          this.interviews = res.data.items.map(i => ({ ...i, loadingMessage: null }));
        })
        .catch(err => (this.error = err))
        .finally(() => {
          this.loadingMessage = null;
        });
    },
    getInterviewRegistrationIdDuplicate() {
      let encodedValue = encodeURIComponent(this.registrationId);
      this.$axios
        .get(`/v1/organizations/${this.investigation.organizationId}/reports/${encodedValue}/duplicate`)
        .then(res => {
          var data = res.data;
          if (data.count > 0 && data.previousDate) {
            this.previousDate = data.previousDate;
            $("#duplicateRegistrationModal").modal("show");
          }
        })
        .catch(err => (this.error = err));
    },
    getActionButtonText(interview) {
      if (!interview.organizationReportId) return "Created";
      var reportStatus = this.reportStatuses.find(l => l.code === interview.organizationReportStatusCode);
      return reportStatus.displayName;
    },
    getActionButtonIcon(interview) {
      if (!interview.organizationReportId) return "pencil-alt";
      if (interview.organizationReportStatusCode === "sent-by-email") return "envelope";
      return "check";
    },
    canSendAnyInterviews() {
      return this.interviews && this.interviews.filter(i => this.canSendInterview(i)).length > 0;
    },
    canReSendIncompleteInterviews() {
      return this.canSendAnyInterviews() == false && this.interviews.filter(i => this.canResendInterview(i)).length > 0;
    },
    canEditInterview(interview) {
      return this.investigation.statusCode === "open" && !interview.organizationReportId && !interview.loadingMessage;
    },
    canDeleteInterview(interview) {
      return this.investigation.statusCode === "open" && !interview.organizationReportId && !interview.loadingMessage;
    },
    canSendInterview(interview) {
      // TODO: review
      return this.investigation.statusCode === "open" && !interview.organizationReportId && !interview.loadingMessage;
    },
    canResendInterview(interview) {
      return (
        this.investigation.statusCode === "open" &&
        interview.organizationReportId &&
        interview.organizationReportStatusCode !== "completed" &&
        interview.organizationReportStatusCode !== "declined" &&
        !interview.loadingMessage
      );
    },
    canStartInterview(interview) {
      return (
        this.investigation.statusCode === "open" &&
        interview.organizationReportStatusCode != "declined" &&
        interview.organizationReportStatusCode != "completed" &&
        !interview.loadingMessage
      );
    },
    canViewReport(interview) {
      return (
        interview.organizationReportStatusCode &&
        interview.organizationReportStatusCode == "completed" &&
        interview.score != "declined" &&
        interview.score != "inconclusive" &&
        interview.score != "none" &&
        !this.isInvestigationProctor
      );
    },
    getInterviewStatusBadgeLabel(interview) {
      if (interview.organizationReportStatusCode == "in-progress") return "In Progress";
      if (interview.organizationReportStatusCode == "declined") return "Declined";
      if (interview.organizationReportStatusCode == "sent-by-email") return "Sent By Email";
      if (interview.organizationReportStatusCode == null) return "Sent By Email";
      if (interview.organizationReportStatusCode == "completed") {
        // TODO: add status cause code to get more granular
        if (interview.score == "declined") return "Declined";
        if (interview.score == "inconclusive") return "Time Out";
        return "Completed";
      }
      return "Unknown";
    },
    getInterviewStatusBadgeStyle(interview) {
      if (interview.organizationReportStatusCode == "in-progress") return "badge-info";
      if (interview.organizationReportStatusCode == "declined") return "badge-danger";
      if (interview.organizationReportStatusCode == "sent-by-email") return "badge-secondary";
      if (interview.organizationReportStatusCode == null) return "badge-secondary";
      if (interview.organizationReportStatusCode == "completed") {
        // TODO: add status cause code to get more granular
        if (interview.score == "declined") return "badge-danger";
        if (interview.score == "inconclusive") return "badge-info";
        return "badge-success";
      }
      return "bg-secondary";
    },
    getReportStatusDisplayName(interview) {
      if (interview && interview.reportStatusDisplayName && interview.reportStatusDisplayName !== "") return interview.reportStatusDisplayName;
      return "---";
    },
    showScore(interview) {
      if (interview && interview.score && interview.score !== "" && interview.score !== "none" && interview.score !== "inconclusive") return true;

      return false;
    },
    getScoreStyle(interview) {
      if (interview.score == "above-conditions") return "above-conditions";
      if (interview.score == "requires-clarification") return "requires-clarification";
      if (interview.score == "below-conditions") return "below-conditions";
      if (interview.score == "inconclusive") return "inconclusive";
      if (interview.score == "declined") return "declined";
      return "";
    },
    async sendAllInterviews() {
      this.isSendingAll = true;
      await this.interviews
        .filter(i => !i.organizationReportId)
        .reduce(async (promise, interview) => {
          await promise; // wait for previous
          await new Promise(r => setTimeout(r, 250)); // throttle
          await this.sendInterview(interview, true);
        }, Promise.resolve())
        .then(() => {
          this.$emit("reload");
          this.isSendingAll = false;
        });
    },
    async sendInterview(interview, suppressReload) {
      interview.loadingMessage = "Sending...";
      await this.$axios
        .post(
          `/v1/organizations/${this.investigation.organizationId}/investigations/${this.investigation.investigationId}/interviews/${interview.organizationInvestigationInterviewId}/send`
        )
        .catch(err => (this.error = err))
        .finally(() => {
          if (!suppressReload) this.$emit("reload");
          interview.loadingMessage = suppressReload ? "Sent!" : null;
        });
    },
    showStartInterviewModal(interview) {
      this.interviewToStart = interview;
      $("#startInterviewOnDeviceModal").modal("show");
    },
    closeStartInterviewModal() {
      this.interviewToStart = null;
      $("#startInterviewOnDeviceModal").modal("hide");
    },
    startInterview() {
      this.loadingMessage = "Starting...";

      this.$axios
        .post(
          `/v1/organizations/${this.investigation.organizationId}/investigations/${this.investigation.investigationId}/interviews/${this.interviewToStart.organizationInvestigationInterviewId}/start`
        )
        .then(res => {
          var data = res.data;
          this.signOut().then(() => (window.location = data.url));
        })
        .catch(err => (this.error = err))
        .finally(() => (this.loadingMessage = null));
    },
    editInterview(interview) {
      this.$refs["edit-modal"].showModal(interview);
    },
    viewReport(interview) {
      let routeData = this.$router.resolve(`/organizations/${interview.organizationId}/reports/${interview.organizationReportId}`);
      window.open(routeData.href, "_blank");
    },
    deleteInterview(interview) {
      interview.loadingMessage = "Deleting...";
      this.$axios
        .delete(
          `/v1/organizations/${this.investigation.organizationId}/investigations/${this.investigation.investigationId}/interviews/${interview.organizationInvestigationInterviewId}`
        )
        .catch(err => (this.error = err))
        .finally(() => {
          this.$emit("reload");
          interview.loadingMessage = null;
        });
    },
    getBgClass(interview) {
      if (interview && interview.hasBeenViewed) return "viewed-interview";
      return "unviewed-interview";
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/base.scss";

.viewed-interview {
  background-color: #f2f6fc;
}

.unviewed-interview {
  background-color: #fff;
  font-weight: 700;
}

.gold-star {
  color: #ffd700;
}

.above-conditions {
  color: #3aaa67;
}

.requires-clarification {
  color: #cccc4a;
}

.below-conditions {
  color: #cc4c56;
}

.inconclusive {
  color: #666666;
}

.declined {
  color: #333333;
}
</style>
